/**
 * /solo/scholarship-orientation — Founder-only orientation for PRSVR Scholarship holders.
 *
 * Access: brandon@prsvr.com / brandoncwmson@gmail.com only.
 * Flow: roster → pick scholar → tuition (sticker vs scholarship) → sign 5-pillar covenant
 *       → auto-comp SOLO scholarship enrollment → jump to Athlete Dashboard.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Loader2, ArrowLeft, ArrowRight, ShieldCheck, Check, X, Award, Trophy,
  User, GraduationCap, MapPin, Lock,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

// ═══════════════════════════════════════════════════════════════════════════
// ROSTER — /solo/scholarship-orientation
// ═══════════════════════════════════════════════════════════════════════════
export const ScholarshipRoster = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/academy/scholarship-orientation/roster', { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Access denied');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <FullPageLoader />;

  if (!data) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-6">
      <div className="text-center max-w-md" data-testid="scholarship-forbidden">
        <Lock className="w-10 h-10 mx-auto text-orange-500 mb-4" />
        <h1 className="text-2xl font-black uppercase tracking-tight mb-2">Founder Access Only</h1>
        <p className="text-sm text-zinc-400">Sign in as brandon@prsvr.com to view the Scholarship Orientation module.</p>
        <Link to="/academy/login" className="inline-block mt-6 bg-orange-500 hover:bg-orange-400 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.2em]">Sign In</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-24 pb-24 px-6" data-testid="scholarship-roster">
      <div className="container mx-auto max-w-4xl">
        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 flex items-center gap-2">
          <Award className="w-3.5 h-3.5" /> PRSVR Founder's Scholarship
        </div>
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02] mt-2">
          Scholarship <span className="text-orange-500">Orientation</span>
        </h1>
        <p className="text-zinc-300 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
          Full-ride orientation for our current cohort of PRSVR Scholarship Holders. Each scholar sees the tuition Vance Johnson pays, then the award that covers it, then signs the Covenant.
        </p>

        <div className="mt-8 grid gap-3">
          {(data.scholars || []).map((s) => (
            <ScholarCard key={s.scholar_key} s={s}
              onOpen={() => navigate(`/academy/orientation/scholarship/${s.scholar_key}`)} />
          ))}
        </div>

        <div className="mt-8 border-2 border-zinc-900 bg-zinc-950 p-4">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-2">Reference Tuition (Sticker)</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-3xl font-black text-white">${data.sticker_tuition?.monthly?.rate?.toLocaleString?.() || '895'}<span className="text-sm text-zinc-500">/mo</span></div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black mt-1">Monthly</div>
            </div>
            <div>
              <div className="text-3xl font-black text-white">${data.sticker_tuition?.annual?.rate?.toLocaleString?.() || '8,000'}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black mt-1">Annual Lump Sum</div>
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-3 italic">Mirrors Vance Johnson's live tuition — the standard PRSVR in-person rate.</div>
        </div>
      </div>
    </div>
  );
};

const ScholarCard = ({ s, onOpen }) => (
  <button onClick={onOpen}
    className={`text-left w-full border-2 p-5 transition-all ${s.covenant_signed ? 'border-orange-500/40 bg-orange-500/5 hover:border-orange-500' : 'border-zinc-800 bg-zinc-950 hover:border-orange-500'}`}
    data-testid={`scholar-card-${s.scholar_key}`}>
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 border-2 border-orange-500/60 flex items-center justify-center text-orange-500 flex-shrink-0">
        <User className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-lg font-black text-white truncate">{s.student_name}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black mt-0.5 flex flex-wrap gap-x-3">
          <span>Grade {s.grade_level}</span>
          <span>{s.state_of_record}</span>
          <span className="truncate">{s.email}</span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        {s.covenant_signed ? (
          <div>
            <div className="text-[9px] uppercase tracking-widest font-black text-orange-500 flex items-center gap-1 justify-end"><Check className="w-3 h-3" /> Signed</div>
            {s.solo_active && <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500 mt-1">SOLO Active</div>}
          </div>
        ) : (
          <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Awaiting Signature</div>
        )}
      </div>
      <ArrowRight className="w-4 h-4 text-orange-500 flex-shrink-0" />
    </div>
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════
// SCHOLAR DETAIL — /solo/scholarship-orientation/:scholarKey
// ═══════════════════════════════════════════════════════════════════════════
export const ScholarOrientation = () => {
  const { scholarKey } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acked, setAcked] = useState({});
  const [signature, setSignature] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [guardianSig, setGuardianSig] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/academy/scholarship-orientation/scholar/${scholarKey}`,
        { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load orientation');
    } finally { setLoading(false); }
  }, [scholarKey]);
  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    if (signature.trim().length < 3) { toast.error('Please type your full name to sign'); return; }
    const required = (data?.code_of_conduct?.pillars || []).map((p) => p.id);
    const missing = required.filter((id) => !acked[id]);
    if (missing.length) { toast.error(`Please acknowledge all ${required.length} pillars`); return; }

    setSubmitting(true);
    try {
      await api.post('/academy/scholarship-orientation/sign', {
        scholar_key: scholarKey,
        typed_signature: signature.trim(),
        guardian_email: guardianEmail.trim() || null,
        guardian_typed_signature: guardianSig.trim() || null,
        acknowledged_pillars: acked,
      }, { headers: authHeaders() });
      toast.success('Covenant signed. Scholarship active.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Signing failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <FullPageLoader />;
  if (!data) return null;

  const s = data.scholar;
  const sticker = data.sticker_tuition;
  const award = data.scholarship_award;
  const coc = data.code_of_conduct;
  const alreadySigned = !!data.covenant;
  const allAcked = (coc?.pillars || []).every((p) => acked[p.id]);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-20 pb-24 px-6" data-testid="scholar-orientation">
      <div className="container mx-auto max-w-3xl">
        <Link to="/academy/orientation/scholarship" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3 h-3" /> Scholarship Roster
        </Link>

        {/* HERO */}
        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 flex items-center gap-2">
          <Award className="w-3.5 h-3.5" /> {award.sponsor}
        </div>
        <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2">
          Welcome to <span className="text-orange-500">PRSVR School</span>, {s.student_name.split(' ')[0]}.
        </h1>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-widest text-zinc-400 font-black">
          <span className="flex items-center gap-1.5"><GraduationCap className="w-3 h-3" /> Grade {s.grade_level}</span>
          <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {s.state_of_record}</span>
          <span>{s.email}</span>
        </div>

        {/* TUITION SIDE-BY-SIDE */}
        <div className="mt-8">
          <SectionTitle>Tuition</SectionTitle>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border-2 border-zinc-800 bg-zinc-950 p-5" data-testid="tuition-sticker">
              <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-2">What Vance Pays</div>
              <div className="text-4xl font-black text-white">${sticker.monthly_rate?.toLocaleString()}<span className="text-sm text-zinc-500">/mo</span></div>
              <div className="text-[11px] text-zinc-500 mt-1">or ${sticker.annual_rate?.toLocaleString()} annual</div>
              <div className="text-[10px] text-zinc-600 mt-3 italic">Reference: {sticker.reference_family}</div>
            </div>
            <div className="border-2 border-orange-500 bg-orange-500/5 p-5" data-testid="tuition-award">
              <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-2 flex items-center gap-1"><Award className="w-3 h-3" /> Your Award</div>
              <div className="flex items-baseline gap-2">
                <div className="text-4xl font-black text-orange-500">${award.monthly_covered?.toLocaleString()}<span className="text-sm">/mo</span></div>
                <span className="text-[10px] uppercase tracking-widest font-black text-orange-500">covered</span>
              </div>
              <div className="text-[11px] text-orange-400 mt-1">Full ${award.annual_covered?.toLocaleString()} annual covered</div>
              <div className="mt-3 pt-3 border-t border-orange-500/30">
                <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">You Owe</div>
                <div className="text-3xl font-black text-white mt-0.5">$0.00</div>
              </div>
            </div>
          </div>
        </div>

        {/* CODE OF CONDUCT */}
        <div className="mt-10">
          <SectionTitle>{coc.title}</SectionTitle>
          <div className="border-2 border-zinc-900 bg-zinc-950 p-5 md:p-6">
            <p className="text-[13px] text-zinc-300 leading-relaxed italic">{coc.preamble}</p>

            <div className="mt-6 space-y-3">
              {(coc.pillars || []).map((p, i) => (
                <PillarRow key={p.id} p={p} index={i + 1}
                  checked={!!acked[p.id] || alreadySigned}
                  disabled={alreadySigned}
                  onToggle={() => setAcked({ ...acked, [p.id]: !acked[p.id] })} />
              ))}
            </div>

            <div className="mt-6 pt-4 border-t-2 border-zinc-900">
              <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-2">Consequences</div>
              <p className="text-[12px] text-zinc-400 leading-relaxed">{coc.consequences}</p>
            </div>
          </div>
        </div>

        {/* SIGNATURE */}
        <div className="mt-10">
          <SectionTitle>{alreadySigned ? 'Signed Covenant' : 'Sign the Covenant'}</SectionTitle>
          {alreadySigned ? (
            <div className="border-2 border-orange-500 bg-orange-500/5 p-6 text-center" data-testid="already-signed">
              <Check className="w-10 h-10 mx-auto text-orange-500 mb-3" />
              <div className="text-[10px] uppercase tracking-[0.4em] font-black text-orange-500">Covenant Signed</div>
              <div className="text-2xl font-black text-white mt-2">{data.covenant.typed_signature}</div>
              <div className="text-[11px] text-zinc-400 mt-2">{new Date(data.covenant.signed_at).toLocaleString()}</div>
              {data.covenant.guardian_typed_signature && (
                <div className="text-[11px] text-zinc-400 mt-3 pt-3 border-t border-orange-500/30">Guardian: <strong className="text-orange-400">{data.covenant.guardian_typed_signature}</strong> ({data.covenant.guardian_email})</div>
              )}
              {data.prsvr_scholarship?.active ? (
                <button onClick={() => navigate('/academy/school')}
                  className="mt-6 bg-orange-500 hover:bg-orange-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-2 mx-auto"
                  data-testid="go-dashboard">
                  <Trophy className="w-3.5 h-3.5" /> Return to School Dashboard <ArrowRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mt-6">Scholarship status pending — refresh</div>
              )}
            </div>
          ) : (
            <div className="border-2 border-zinc-900 bg-zinc-950 p-5 md:p-6">
              <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">
                Scholar Full Name (typed = signature)
              </label>
              <input value={signature} onChange={(e) => setSignature(e.target.value)}
                placeholder={s.student_name}
                className="w-full bg-black border-2 border-zinc-800 px-3 py-3 text-lg font-black tracking-tight text-orange-400 focus:border-orange-500 outline-none mb-4"
                style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
                data-testid="scholar-signature" />

              <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">
                Guardian Email (optional — for milestone alerts)
              </label>
              <input type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)}
                placeholder="guardian@example.com"
                className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none mb-3"
                data-testid="guardian-email" />

              <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">
                Guardian Signature (optional)
              </label>
              <input value={guardianSig} onChange={(e) => setGuardianSig(e.target.value)}
                placeholder="Guardian full name"
                className="w-full bg-black border-2 border-zinc-800 px-3 py-3 text-lg font-black tracking-tight text-orange-400 focus:border-orange-500 outline-none mb-4"
                style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
                data-testid="guardian-signature" />

              <button onClick={sign}
                disabled={submitting || !allAcked || signature.trim().length < 3}
                className="w-full bg-orange-500 hover:bg-orange-400 text-black py-4 text-[12px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="sign-covenant-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Sign Covenant & Confirm Scholarship
              </button>
              {(!allAcked || signature.trim().length < 3) && (
                <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mt-3 text-center">
                  {!allAcked ? 'Acknowledge all 5 pillars' : 'Type scholar\'s full name to sign'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PillarRow = ({ p, index, checked, disabled, onToggle }) => (
  <button onClick={onToggle} disabled={disabled}
    className={`text-left w-full border-2 p-4 transition-all ${checked ? 'border-orange-500 bg-orange-500/5' : 'border-zinc-800 bg-black hover:border-zinc-600'} ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
    data-testid={`pillar-${p.id}`}>
    <div className="flex items-start gap-3">
      <div className={`w-6 h-6 rounded-sm border-2 flex-shrink-0 flex items-center justify-center ${checked ? 'border-orange-500 bg-orange-500 text-black' : 'border-zinc-700'}`}>
        {checked && <Check className="w-3.5 h-3.5" />}
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest font-black text-orange-500">Pillar {index}</div>
        <div className="text-sm font-black uppercase tracking-tight text-white mt-0.5">{p.title}</div>
        <p className="text-[12px] text-zinc-400 leading-relaxed mt-2">{p.body}</p>
      </div>
    </div>
  </button>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-[10px] tracking-[0.5em] uppercase font-black text-white mb-3">{children}</h2>
);

const FullPageLoader = () => (
  <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
  </div>
);

export default ScholarshipRoster;
